import { del, get, post, put } from 'src/services/axios.service';
import {
  IAssessment,
  IAssessmentAnswer,
  IAssessmentResult,
  IAttendAssessment
} from './assessment.interface';
import { lime } from '@mui/material/colors';

export const getAssessmentList = async (
  filterData: {
    limit?: number;
    offset?: number;
    name?: string;
    date?: string;
    status?: string;
  } = {}
): Promise<IAssessment[]> => {
  try {
    const { limit, offset, name, date, status } = filterData;
    const startAfter = date ? setStartTimeToMidnight(date) : undefined;
    const endBefore = date ? setEndTimeToMidnight(date) : undefined;
    const assessmentList = await get<IAssessment[]>('/assessment', {
      params: { limit, offset, name, startAfter, endBefore, status }
    });
    return assessmentList;
  } catch (error) {
    throw error;
  }
};
const setStartTimeToMidnight = (dateTime: string): string => {
  const datePart = dateTime.split('T')[0];
  return `${datePart}T00:00:00.000Z`;
};

const setEndTimeToMidnight = (dateTime: string): string => {
  const datePart = dateTime.split('T')[0];
  return `${datePart}T23:59:59.999Z`;
};

export const getCurrentAssessmentList = async (
  pagination: { limit?: number; offset?: number } = {}
): Promise<IAssessment[]> => {
  try {
    const today = new Date();
    const modifiedStartAfter = setStartTimeToMidnight(today.toISOString());
    const modifiedEndBefore = setEndTimeToMidnight(today.toISOString());
    const currentDateTime = today.toISOString();

    const { limit, offset } = pagination;
    const assessmentList = await get<IAssessment[]>('/assessment', {
      params: {
        limit,
        offset,
        status: 'ACTIVE',
        startAfter: modifiedStartAfter,
        endBefore: modifiedEndBefore,
        endAfter: currentDateTime
      }
    });
    return assessmentList;
  } catch (error) {
    throw error;
  }
};

export const getUpcomingAssessmentList = async (
  currentTime: string,
  pagination: { limit?: number; offset?: number } = {}
): Promise<IAssessment[]> => {
  try {
    const modifiedStartAfter = setStartTimeToMidnight(currentTime);
    const { limit, offset } = pagination;
    const assessmentList = await get<IAssessment[]>('/assessment', {
      params: {
        limit,
        offset,
        status: 'ACTIVE',
        startAfter: modifiedStartAfter,
        endAfter: currentTime
      }
    });
    return assessmentList;
  } catch (error) {
    throw error;
  }
};

export const getCompletedAssessmentList = async (
  endBefore: string,
  pagination: { limit?: number; offset?: number } = {}
): Promise<IAssessment[]> => {
  try {
    const { limit, offset } = pagination;
    const assessmentList = await get<IAssessment[]>('/assessment', {
      params: {
        limit,
        offset,
        status: 'ACTIVE',
        endBefore
      }
    });
    return assessmentList;
  } catch (error) {
    throw error;
  }
};

export const getCompletedAssessmentListReport = async (params: {
  startAfter?: string;
  endBefore?: string;
  name?: string;
  limit?: number;
  offset?: number;
}): Promise<IAssessment[]> => {
  try {
    const { limit, offset, name, startAfter, endBefore } = params;
    const assessmentList = await get<IAssessment[]>('/assessment', {
      params: {
        limit,
        offset,
        status: 'ACTIVE',
        endBefore, // Include endBefore parameter by default or from params
        ...(startAfter && { startAfter }),
        ...(name && { name }),
      },
    });
    return assessmentList;
  } catch (error) {
    throw error;
  }
};

export const getAssessmentById = async (
  assessmentId: number
): Promise<IAssessment | null> => {
  try {
    const assessment = await get<IAssessment>(`/assessment/${assessmentId}`);
    return assessment;
  } catch (error) {
    throw error;
  }
};

export const createAssessment = async (
  assessmentData: Partial<IAssessment>
): Promise<IAssessment> => {
  try {
    const createdAssessment = await post<IAssessment>(
      '/assessment',
      assessmentData
    );
    return createdAssessment;
  } catch (error) {
    throw error;
  }
};

export const createGradeSubject = async (
  assessmentId: number,
  gradeSubjectData: Partial<IAssessment>
): Promise<IAssessment> => {
  try {
    const createdAssessment = await post<IAssessment>(
      `/assessment/${assessmentId}/grade-subject`,
      gradeSubjectData
    );
    return createdAssessment;
  } catch (error) {
    throw error;
  }
};

export const createGeneralCategory = async (
  assessmentId: number,
  generalCategoryData: Partial<IAssessment>
): Promise<IAssessment> => {
  try {
    const createdAssessment = await post<IAssessment>(
      `/assessment/${assessmentId}/general-category`,
      generalCategoryData
    );
    return createdAssessment;
  } catch (error) {
    throw error;
  }
};

export const updateAssessment = async (
  assessmentId: number,
  assessmentData: Partial<IAssessment>
): Promise<IAssessment | null> => {
  try {
    const updatedAssessment = await put<IAssessment>(
      `/assessment/${assessmentId}`,
      assessmentData
    );
    return updatedAssessment;
  } catch (error) {
    throw error;
  }
};

export const updateGradeSubject = async (
  assessmentId: number,
  assessmentGradeSubjectId: number,
  assessmentData: IAssessment
): Promise<IAssessment | null> => {
  try {
    const updateGradeSubject = await put<IAssessment>(
      `/assessment/${assessmentId}/grade-subject/${assessmentGradeSubjectId}`,
      assessmentData
    );
    return updateGradeSubject;
  } catch (error) {
    throw error;
  }
};

export const updateGeneralCategory = async (
  assessmentId: number,
  assesmentGeneralCategoryId: number,
  assessmentData: IAssessment
): Promise<IAssessment | null> => {
  try {
    const updateGeneralCategory = await put<IAssessment>(
      `/assessment/${assessmentId}/general-category/${assesmentGeneralCategoryId}`,
      assessmentData
    );
    return updateGeneralCategory;
  } catch (error) {
    throw error;
  }
};

export const deleteAssessment = async (
  assessmentId: number
): Promise<boolean> => {
  try {
    const deleteResponse = await del<any>(`/assessment/${assessmentId}`);
    return (
      deleteResponse?.status === true && deleteResponse?.data?.deleted === true
    );
  } catch (error) {
    throw error;
  }
};

export const deleteGradeSubject = async (
  assessmentId: number,
  assessmentGradeSubjectId: number
): Promise<boolean> => {
  try {
    const deleteResponse = await del<any>(
      `/assessment/${assessmentId}/grade-subject/${assessmentGradeSubjectId}`
    );
    return (
      deleteResponse?.status === true && deleteResponse?.data?.deleted === true
    );
  } catch (error) {
    throw error;
  }
};

export const deleteGeneralCategory = async (
  assessmentId: number,
  assesmentGeneralCategoryId: number
): Promise<boolean> => {
  try {
    const deleteResponse = await del<any>(
      `/assessment/${assessmentId}/general-category/${assesmentGeneralCategoryId}`
    );
    return (
      deleteResponse?.status === true && deleteResponse?.data?.deleted === true
    );
  } catch (error) {
    throw error;
  }
};

export const CreateAttendAssessment = async (
  teacherId: number,
  assessmentId: number
): Promise<IAttendAssessment> => {
  try {
    const createAttendAssessment = await post<IAttendAssessment>(
      `/teacher/${teacherId}/assessment/${assessmentId}`,
      { status: 'Start' }
    );
    return createAttendAssessment;
  } catch (error) {
    throw error;
  }
};

export const getTeacherCompletedAssessment =
  async (): Promise<IAttendAssessment> => {
    try {
      const completedAttendAssessment = await get<IAttendAssessment>(
        `/teacher/attended-assessment`
      );
      return completedAttendAssessment;
    } catch (error) {
      throw error;
    }
  };

export const updateAttendAssessment = async (
  teacherId: number,
  assessmentId: number
): Promise<IAttendAssessment> => {
  try {
    const createAttendAssessment = await put<IAttendAssessment>(
      `/teacher/${teacherId}/assessment/${assessmentId}`,
      { status: 'End' }
    );
    return createAttendAssessment;
  } catch (error) {
    throw error;
  }
};

export const terminateAttendAssessment = async (
  teacherId: number,
  assessmentId: number
): Promise<IAttendAssessment> => {
  try {
    const createAttendAssessment = await put<IAttendAssessment>(
      `/teacher/${teacherId}/assessment/${assessmentId}`,
      { status: 'Terminate' }
    );
    return createAttendAssessment;
  } catch (error) {
    throw error;
  }
};

export const getAssessmentTerminastionResult = async (
  teacherId: number,
  assessmentId: number
): Promise<any> => {
  try {
    const assessmentResultList = await get<any>(
      `/teacher/${teacherId}/assessment/${assessmentId}`,
      {}
    );
    return assessmentResultList;
  } catch (error) {
    throw error;
  }
};

export const getAssessmentQuestionStaticsData = async (
  attendAssessmentId: number
): Promise<any> => {
  try {
    const assessmentResultList = await get<any>(
      `/assessment/attendedAssessment/${attendAssessmentId}`,
      {}
    );
    return assessmentResultList;
  } catch (error) {
    throw error;
  }
};

export const getAssessmentScoreData = async (
  teacherId: number,
  assessmentId: number
): Promise<any> => {
  try {
    const assessmentScoreData = await get<any>(
      `/teacher/${teacherId}/attended-assessment/${assessmentId}`,
      {}
    );
    return assessmentScoreData;
  } catch (error) {
    throw error;
  }
};

export const getAssessmentCategoryData = async (
  assessmentId: number
): Promise<any> => {
  try {
    const assessmentScoreData = await get<any>(`stats/tag/${assessmentId}`, {});
    return assessmentScoreData;
  } catch (error) {
    throw error;
  }
};

export const getAssessmentForTeacherCategoryData = async (
  assessmentId: number,
  teacherId: number
): Promise<any> => {
  try {
    const assessmentScoreData = await get<any>(`stats/tag/${assessmentId}/teacher/${teacherId}`, {});
    return assessmentScoreData;
  } catch (error) {
    throw error;
  }
};

export const getAssessmentCategoryDataDashboard = async (): Promise<any> => {
  try {
    const assessmentScoreData = await get<any>(`stats/tag`, {});
    return assessmentScoreData;
  } catch (error) {
    throw error;
  }
};

export const CreateAssessmentAnswer = async (
  teacherId: number,
  assessmentId: number,
  questionAnswers: Partial<IAssessmentAnswer>[]
): Promise<IAssessmentAnswer> => {
  try {
    const createAssessmentAnswer = await post<IAssessmentAnswer>(
      `/teacher/${teacherId}/assessment/${assessmentId}/answers`,
      { questionAnswers: questionAnswers }
    );
    return createAssessmentAnswer;
  } catch (error) {
    throw error;
  }
};

export const UpdateAttendAssessmentData = async (
  teacherId: number,
  assessmentId: number,
  assessmentAnswerData: Partial<IAssessmentAnswer>
): Promise<IAssessmentAnswer> => {
  try {
    const createAssessmentAnswer = await put<IAssessmentAnswer>(
      `/teacher/${teacherId}/assessment/${assessmentId}/answer`,
      assessmentAnswerData
    );
    return createAssessmentAnswer;
  } catch (error) {
    throw error;
  }
};

export const getAssessmentAnswer = async (
  teacherId: number,
  assessmentId: number
): Promise<IAssessmentAnswer[]> => {
  try {
    const assessmentAnswerList = await get<IAssessmentAnswer[]>(
      `/teacher/${teacherId}/assessment/${assessmentId}/answer`,
      {}
    );
    return assessmentAnswerList;
  } catch (error) {
    throw error;
  }
};

export const getAssessmentQuestionList = async (
  assessmentId: number
): Promise<IAssessment[]> => {
  try {
    const assessmentQuestionList = await get<IAssessment[]>(
      `/assessment/${assessmentId}/questions`,
      {}
    );
    return assessmentQuestionList;
  } catch (error) {
    throw error;
  }
};

export const getAssessmentResult = async (
  teacherId: number,
  assessmentId: number
): Promise<any> => {
  try {
    const assessmentResultList = await get<any>(
      `/assessment/${assessmentId}/result/${teacherId}`,
      {}
    );
    return assessmentResultList;
  } catch (error) {
    throw error;
  }
};

export const getTeacherAssessmentResult = async (
  assessmentId: number
): Promise<any> => {
  try {
    const assessmentResultList = await get<any>(
      `/assessment/${assessmentId}/result`,
      {}
    );
    return assessmentResultList;
  } catch (error) {
    throw error;
  }
};

export const getTeacherCompletedAssessmentByTeacherId = async (
  teacherId: number,
  pagination: { limit?: number; offset?: number } = {}
): Promise<IAttendAssessment[]> => {
  try {
    const { limit, offset } = pagination;
    const completedAssessment = await get<IAttendAssessment[]>(
      `/teacher/${teacherId}/attended-assessment`,
      {
        params: { limit, offset }
      }
    );
    return completedAssessment;
  } catch (error) {
    throw error;
  }
};

export const createRepublishAssessment = async (
  assessmentId: number,
  assessmentData: Partial<any>
): Promise<any> => {
  try {
    const createdAssessment = await post<any>(
      `/assessment-republish/${assessmentId}`,
      assessmentData
    );
    return createdAssessment;
  } catch (error) {
    throw error;
  }
};

export const getCertificate = async (assessmentId: number): Promise<any[]> => {
  try {
    const certificate = await get<any[]>(
      `/assessment/${assessmentId}/certificate`
    );
    return certificate;
  } catch (error) {
    throw error;
  }
};


export const getGeneralCategories = async (): Promise<any[]> => {
  try {
    const response = await get<{ data: { generalcategory: any[] } }>('/general-category', { params: { limit: 0, offset: 0 } });
    return response.data.generalcategory;

  } catch (error) {
    throw error;
  }
};






export const getSubject = async (): Promise<any[]> => {
  try {

    const response = await get<{ data: { subject: any[] } }>('/subject', {
      params: { limit: 0, offset: 0 }
    });
    return response.data.subject;
  } catch (error) {
    throw error;
  }
};






export const getGrade = async (): Promise<any[]> => {
  try {
    const response = await get<{ data: { grade: any[] } }>('/grade', {
      params: { limit: 0, offset: 0 }
    });
    return response.data.grade;
  } catch (error) {
    throw error;
  }
};




export const createSelfAssessment = async (
  assessmentData: Partial<any>
): Promise<any> => {
  try {
    const createdAssessment = await post<any>(
      "assessment/request",
      assessmentData
    );
    return createdAssessment;
  } catch (error) {
    throw error;
  }
};
